import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	PlusIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline'
import { useLoadboardTheme } from './loadboardTheme'

function SearchTabs({ tabs, activeTabId, onSelect, onClose, onAdd, newCountsByTab = {} }) {
	const { theme, tokens } = useLoadboardTheme()
	const isLight = theme === 'light'

	const scrollRef = useRef(null)
	const tabRefs = useRef(new Map())
	const prevTabsLengthRef = useRef(tabs.length)

	const [canScrollLeft, setCanScrollLeft] = useState(false)
	const [canScrollRight, setCanScrollRight] = useState(false)

	const updateScrollState = useCallback(() => {
		const c = scrollRef.current
		if (!c) return
		setCanScrollLeft(c.scrollLeft > 0)
		setCanScrollRight(c.scrollLeft + c.clientWidth < c.scrollWidth - 1)
	}, [])

	useEffect(() => {
		const c = scrollRef.current
		if (!c) return
		updateScrollState()
		c.addEventListener('scroll', updateScrollState, { passive: true })
		const ro = new ResizeObserver(updateScrollState)
		ro.observe(c)
		return () => {
			c.removeEventListener('scroll', updateScrollState)
			ro.disconnect()
		}
	}, [updateScrollState])

	useEffect(() => {
		updateScrollState()
	}, [tabs.length, updateScrollState])

	const scrollAllLeft = () => {
		scrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' })
	}
	const scrollAllRight = () => {
		const c = scrollRef.current
		if (c) c.scrollTo({ left: c.scrollWidth, behavior: 'smooth' })
	}

	const arrowBtn = isLight
		? 'bg-white text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200 shadow-md'
		: 'bg-gray-800 text-gray-200 hover:bg-gray-700 ring-1 ring-gray-700 shadow-md'

	useEffect(() => {
		const container = scrollRef.current
		if (!container) return

		const tabAdded = tabs.length > prevTabsLengthRef.current
		prevTabsLengthRef.current = tabs.length

		const lastTabId = tabs.length > 0 ? tabs[tabs.length - 1].id : null
		const activeIsLast = activeTabId === lastTabId

		if (tabAdded || activeIsLast) {
			container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' })
			return
		}

		const el = tabRefs.current.get(activeTabId)
		if (!el) return

		const containerRect = container.getBoundingClientRect()
		const elRect = el.getBoundingClientRect()

		if (elRect.left < containerRect.left) {
			container.scrollBy({ left: elRect.left - containerRect.left, behavior: 'smooth' })
		} else if (elRect.right > containerRect.right) {
			container.scrollBy({ left: elRect.right - containerRect.right, behavior: 'smooth' })
		}
	}, [activeTabId, tabs.length])

	return (
		<>
			<style>{`
				.ll-tabs-scroll::-webkit-scrollbar { display: none; }
				.ll-tabs-scroll { scrollbar-width: none; -ms-overflow-style: none; }
			`}</style>
			<div className="relative">
				{canScrollLeft && (
					<button
						type="button"
						onClick={scrollAllLeft}
						className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-6 h-6 rounded-full transition-colors ${arrowBtn}`}
						aria-label="Scroll tabs to start"
					>
						<ChevronLeftIcon className="h-3.5 w-3.5 stroke-[2.5]" />
					</button>
				)}
				{canScrollRight && (
					<button
						type="button"
						onClick={scrollAllRight}
						className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-6 h-6 rounded-full transition-colors ${arrowBtn}`}
						aria-label="Scroll tabs to end"
					>
						<ChevronRightIcon className="h-3.5 w-3.5 stroke-[2.5]" />
					</button>
				)}
				<div ref={scrollRef} className="flex items-center gap-1 overflow-x-auto ll-tabs-scroll">
			{tabs.map(tab => {
				const isActive = tab.id === activeTabId
				const newCount = newCountsByTab[tab.id] || 0
				const showBadge = !isActive && newCount > 0
				const subtitleClass = isActive
					? isLight ? 'text-gray-500' : 'text-gray-400'
					: tokens.tabSubtitle
				return (
					<button
						key={tab.id}
						ref={node => {
							if (node) tabRefs.current.set(tab.id, node)
							else tabRefs.current.delete(tab.id)
						}}
						type="button"
						onClick={() => onSelect(tab.id)}
						className={`group relative flex items-center gap-2 min-w-[158px] max-w-[231px] pl-3 pr-2 py-[5px] rounded-lg text-left transition-colors ${
							isActive ? tokens.tabActive : tokens.tabInactive
						}`}
					>
						<div className="flex-1 min-w-0">
							<div className="text-[11px] font-medium leading-tight truncate">
								{tab.title || 'Anywhere'}
							</div>
							<div className={`text-[11px] leading-tight truncate ${subtitleClass}`}>
								{tab.subtitle || 'Anywhere'}
							</div>
						</div>

						{showBadge && (
							<span
								className={`shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold ${
									isLight
										? 'bg-blue-500 text-white'
										: 'bg-blue-400 text-gray-900'
								}`}
								title={`${newCount} new load${newCount === 1 ? '' : 's'}`}
							>
								{newCount > 99 ? '99+' : newCount}
							</span>
						)}

						<span
							role="button"
							tabIndex={0}
							onClick={e => {
								e.stopPropagation()
								onClose(tab.id)
							}}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.stopPropagation()
									e.preventDefault()
									onClose(tab.id)
								}
							}}
							className={`ml-auto -mr-0.5 p-0.5 rounded ${tokens.closeTab} ${
								isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
							}`}
							aria-label="Close tab"
						>
							<XMarkIcon className="h-3.5 w-3.5" />
						</span>
					</button>
				)
			})}

			<button
				type="button"
				onClick={() => onAdd()}
				className={`ml-3 flex items-center justify-center w-8 h-6 rounded-md shadow-sm shrink-0 transition-colors ${tokens.addTabBtn}`}
				aria-label="Add search tab"
			>
				<PlusIcon className="h-4 w-4 stroke-[2.5]" />
			</button>
			</div>
			</div>
		</>
	)
}

export default SearchTabs
